import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2, XCircle, Clock, Building2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AbfrageInfo, tageSeit, ERINNERUNG_TAGE } from "@/components/ERechnungStammdaten";

const faellig = (am, beantwortet) => !beantwortet && am && tageSeit(am) >= ERINNERUNG_TAGE;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const Kachel = ({ wert, label, farbe, testid }) => (
  <div className="rounded-xl border border-[#E0D6CA] bg-white px-4 py-3" data-testid={testid}>
    <div className="text-2xl font-semibold tabular-nums" style={{ color: farbe }}>{wert}</div>
    <div className="text-[11px] uppercase tracking-wider text-[#8E7B71]">{label}</div>
  </div>
);

const AbfrageLeiste = ({ anzahl, busy, onSenden, text, testid }) => (
  <div className="flex flex-wrap items-center gap-3 my-4">
    <Button disabled={!anzahl || busy} onClick={onSenden} className="bg-[#9A3B2E] hover:bg-[#7F2F25] text-white" data-testid={testid}>
      {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
      {text} ({anzahl})
    </Button>
    <span className="text-xs text-[#8E7B71]">Versand nur auf Knopfdruck – nichts geht automatisch raus.</span>
  </div>
);

const B2BZeile = ({ pe, ausgewaehlt, toggle, onSaved }) => {
  const [email, setEmail] = useState(pe.rechnung_email || "");
  const speichern = async () => {
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${pe.id}/rechnung-email`, { rechnung_email: email });
      toast.success(email ? "Rechnungs-E-Mail gespeichert" : "Rechnungs-E-Mail entfernt");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
  };
  return (
    <tr className="border-b border-[#F1E8DF] align-top" data-testid={`b2b-row-${pe.id}`}>
      <td className="py-2 pr-2"><Checkbox checked={ausgewaehlt} onCheckedChange={toggle} data-testid={`b2b-check-${pe.id}`} /></td>
      <td className="py-2 pr-3"><div className="font-medium">{pe.einrichtungsname}</div><div className="text-[11px] text-[#8E7B71]">{pe.ort}</div></td>
      <td className="py-2 pr-3 text-xs text-[#8E7B71]">{pe.kontakt_email || "—"}</td>
      <td className="py-2 pr-3">
        <div className="flex gap-1.5">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="rechnung@…" className="h-8 text-sm min-w-[200px]" data-testid={`b2b-email-${pe.id}`} />
          <Button size="sm" variant="outline" className="h-8" onClick={speichern} disabled={(pe.rechnung_email || "") === email} data-testid={`b2b-save-${pe.id}`}>Speichern</Button>
        </div>
        {pe.rechnung_email && <div className="text-[10px] text-emerald-700 mt-1">{pe.rechnung_email_quelle} · {dt(pe.rechnung_email_am)}</div>}
      </td>
      <td className="py-2 text-[11px] text-[#8E7B71]" data-testid={`b2b-abfrage-${pe.id}`}>
        <AbfrageInfo am={pe.rechnung_email_abfrage_am} anzahl={pe.rechnung_email_abfrage_anzahl} beantwortet={!!pe.rechnung_email} />{pe.rechnung_email_abfrage_an && <><br />an {pe.rechnung_email_abfrage_an}</>}
      </td>
    </tr>
  );
};

const STATUS = {
  ja: { label: "Zustimmung liegt vor", icon: CheckCircle2, farbe: "#4F6650" },
  nein: { label: "Abgelehnt – Postversand", icon: XCircle, farbe: "#C2452F" },
  offen: { label: "Keine Zustimmung – Postversand", icon: Clock, farbe: "#B7791F" },
};

const nachweisText = (n) => {
  if (!n) return "";
  const teile = [n.quelle, dt(n.zeitpunkt)];
  if (n.beleg) teile.push(`Beleg: ${n.beleg}`);
  if (n.erfasst_von) teile.push(`erfasst von ${n.erfasst_von}`);
  if (n.ip) teile.push(`IP ${n.ip}`);
  return teile.filter(Boolean).join(" · ");
};

const SZZeile = ({ tn, ausgewaehlt, toggle, onSaved }) => {
  const st = STATUS[tn.email_rechnung_einwilligung] || STATUS.offen;
  const Icon = st.icon;
  const setzen = async (status) => {
    let beleg = "";
    if (status === "ja") {
      beleg = window.prompt("Wie liegt die Zustimmung vor? (z. B. „Anmeldeformular unterschrieben am …“)", "");
      if (beleg === null) return;
    } else if (!window.confirm(`Zustimmung für ${tn.vorname} ${tn.nachname} entfernen? Rechnungen müssen dann per Post raus.`)) return;
    try {
      await axios.put(`${API}/teilnehmer/${tn.id}/email-einwilligung`, { status, beleg });
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
  };
  return (
    <tr className="border-b border-[#F1E8DF] align-top" data-testid={`sz-row-${tn.id}`}>
      <td className="py-2 pr-2"><Checkbox checked={ausgewaehlt} onCheckedChange={toggle} data-testid={`sz-check-${tn.id}`} /></td>
      <td className="py-2 pr-3 font-medium">{tn.vorname} {tn.nachname}</td>
      <td className="py-2 pr-3 text-xs text-[#8E7B71]">{tn.email || "—"}</td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          <Checkbox checked={tn.email_rechnung_einwilligung === "ja"} onCheckedChange={(v) => setzen(v ? "ja" : "nein")} data-testid={`sz-einwilligung-${tn.id}`} />
          <span className="text-xs font-semibold flex items-center gap-1" style={{ color: st.farbe }} data-testid={`sz-status-${tn.id}`}><Icon size={13} /> {st.label}</span>
        </div>
        {tn.email_rechnung_nachweis && <div className="text-[10px] text-[#8E7B71] mt-1 max-w-[360px]" data-testid={`sz-nachweis-${tn.id}`}>Nachweis: {nachweisText(tn.email_rechnung_nachweis)}</div>}
      </td>
      <td className="py-2 text-[11px] text-[#8E7B71]" data-testid={`sz-abfrage-${tn.id}`}><AbfrageInfo am={tn.email_rechnung_abfrage_am} anzahl={tn.email_rechnung_abfrage_anzahl} beantwortet={!!tn.email_rechnung_einwilligung} /></td>
    </tr>
  );
};

const useAuswahl = () => {
  const [ids, setIds] = useState([]);
  const toggle = (id) => setIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  return { ids, setIds, toggle };
};

const Kopf = ({ spalten, alle, setAlle }) => (
  <thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#8E7B71] border-b border-[#E0D6CA]">
    <th className="py-2 pr-2"><Checkbox checked={alle} onCheckedChange={setAlle} /></th>
    {spalten.map((s) => <th key={s} className="py-2 pr-3 font-semibold">{s}</th>)}
  </tr></thead>
);

export default function ERechnungVersand() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterB2B, setFilterB2B] = useState("ohne");
  const [filterSZ, setFilterSZ] = useState("offen");
  const b2b = useAuswahl();
  const sz = useAuswahl();
  const laden = useCallback(() => axios.get(`${API}/erechnung-versand/uebersicht`).then((r) => setD(r.data)).catch(() => toast.error("Übersicht konnte nicht geladen werden")), []);
  useEffect(() => { laden(); }, [laden]);

  const b2bListe = useMemo(() => (d?.b2b || []).filter((p) => filterB2B === "alle" || (filterB2B === "erinnerung" ? faellig(p.rechnung_email_abfrage_am, p.rechnung_email) : !p.rechnung_email)), [d, filterB2B]);
  const szListe = useMemo(() => (d?.selbstzahler || []).filter((t) => filterSZ === "alle" || (filterSZ === "erinnerung" ? faellig(t.email_rechnung_abfrage_am, t.email_rechnung_einwilligung) : t.email_rechnung_einwilligung !== "ja")), [d, filterSZ]);
  const b2bFaellig = (d?.b2b || []).filter((p) => faellig(p.rechnung_email_abfrage_am, p.rechnung_email)).length;
  const szFaellig = (d?.selbstzahler || []).filter((t) => faellig(t.email_rechnung_abfrage_am, t.email_rechnung_einwilligung)).length;

  const abfrage = async (art, ids, reset) => {
    if (!window.confirm(`Jetzt ${ids.length} Abfrage-Mail(s) wirklich versenden?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/erechnung-versand/abfrage/${art}`, { ids });
      toast.success(`${r.data.gesendet.length} Mail(s) gesendet`);
      if (r.data.fehler.length) toast.error(`${r.data.fehler.length} nicht gesendet: ${r.data.fehler.map((f) => `${f.name} (${f.grund})`).join(", ")}`, { duration: 12000 });
      reset([]);
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Versand"); }
    setBusy(false);
  };

  if (!d) return <div className="page-container text-sm text-[#8E7B71]">Lade…</div>;
  const mitAdresse = d.b2b.filter((p) => p.rechnung_email).length;
  const mitZustimmung = d.selbstzahler.filter((t) => t.email_rechnung_einwilligung === "ja").length;

  return (
    <div className="page-container" data-testid="erechnung-versand-page">
      <p className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#9A3B2E]">E-Rechnungspflicht ab 01.01.2027</p>
      <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>E-Rechnung & Versand</h1>
      <p className="text-sm text-[#8E7B71] mt-1 max-w-3xl">Rechnungen an Einrichtungen gehen beim Versand automatisch als E-Rechnung (ZUGFeRD-PDF) an die hinterlegte Rechnungs-E-Mail. Selbstzahler bekommen Rechnungen nur mit Zustimmung per E-Mail – sonst ist der E-Mail-Versand gesperrt und die Rechnung muss per Post raus.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-5">
        <Kachel wert={`${mitAdresse}/${d.b2b.length}`} label="Einrichtungen mit Rechnungs-E-Mail" farbe="#4F6650" testid="kpi-b2b" />
        <Kachel wert={d.b2b.length - mitAdresse} label="Einrichtungen ohne Adresse" farbe="#B7791F" testid="kpi-b2b-offen" />
        <Kachel wert={`${mitZustimmung}/${d.selbstzahler.length}`} label="Selbstzahler mit Zustimmung" farbe="#4F6650" testid="kpi-sz" />
        <Kachel wert={d.selbstzahler.length - mitZustimmung} label="Selbstzahler – Postversand" farbe="#C2452F" testid="kpi-sz-post" />
      </div>
      <Tabs defaultValue="b2b">
        <TabsList>
          <TabsTrigger value="b2b" data-testid="tab-b2b"><Building2 size={14} className="mr-1.5" /> Einrichtungen (B2B)</TabsTrigger>
          <TabsTrigger value="sz" data-testid="tab-sz"><User size={14} className="mr-1.5" /> Selbstzahler</TabsTrigger>
        </TabsList>
        <TabsContent value="b2b">
          <div className="flex gap-2 mt-3 text-xs">
            {[["ohne", "Ohne Rechnungs-E-Mail"], ["erinnerung", `Erinnerung fällig (${b2bFaellig})`], ["alle", "Alle"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilterB2B(k)} className={`px-3 py-1 rounded-full border ${filterB2B === k ? "bg-[#3D3835] text-white" : "bg-white"}`} data-testid={`b2b-filter-${k}`}>{l}</button>
            ))}
          </div>
          <AbfrageLeiste anzahl={b2b.ids.length} busy={busy} onSenden={() => abfrage("b2b", b2b.ids, b2b.setIds)} text={filterB2B === "erinnerung" ? "Erinnerung senden" : "Rechnungs-E-Mail abfragen"} testid="b2b-abfrage-senden" />
          <div className="overflow-x-auto rounded-xl border border-[#E0D6CA] bg-white p-3">
            <table className="w-full text-sm">
              <Kopf spalten={["Einrichtung", "Kontakt (Abfrage geht an)", "Rechnungs-E-Mail", "Abfrage"]} alle={b2bListe.length > 0 && b2b.ids.length === b2bListe.length} setAlle={(v) => b2b.setIds(v ? b2bListe.map((p) => p.id) : [])} />
              <tbody>{b2bListe.map((pe) => <B2BZeile key={pe.id} pe={pe} ausgewaehlt={b2b.ids.includes(pe.id)} toggle={() => b2b.toggle(pe.id)} onSaved={laden} />)}</tbody>
            </table>
            {!b2bListe.length && <p className="text-sm text-[#8E7B71] p-4">Keine Einträge.</p>}
          </div>
        </TabsContent>
        <TabsContent value="sz">
          <div className="flex gap-2 mt-3 text-xs">
            {[["offen", "Ohne Zustimmung"], ["erinnerung", `Erinnerung fällig (${szFaellig})`], ["alle", "Alle"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilterSZ(k)} className={`px-3 py-1 rounded-full border ${filterSZ === k ? "bg-[#3D3835] text-white" : "bg-white"}`} data-testid={`sz-filter-${k}`}>{l}</button>
            ))}
          </div>
          <AbfrageLeiste anzahl={sz.ids.length} busy={busy} onSenden={() => abfrage("selbstzahler", sz.ids, sz.setIds)} text={filterSZ === "erinnerung" ? "Erinnerung senden" : "Zustimmung per Mail anfragen"} testid="sz-abfrage-senden" />
          {d.selbstzahler_ohne_verknuepfung > 0 && (
            <p className="text-xs text-[#B7791F] mb-2" data-testid="sz-ohne-verknuepfung">{d.selbstzahler_ohne_verknuepfung} Selbstzahler-Rechnung(en) sind keinem Teilnehmer zugeordnet – dafür ist E-Mail-Versand gesperrt, bis die Rechnung verknüpft ist.</p>
          )}
          <div className="overflow-x-auto rounded-xl border border-[#E0D6CA] bg-white p-3">
            <table className="w-full text-sm">
              <Kopf spalten={["Teilnehmer", "E-Mail", "Rechnung per E-Mail erlaubt", "Anfrage"]} alle={szListe.length > 0 && sz.ids.length === szListe.length} setAlle={(v) => sz.setIds(v ? szListe.map((t) => t.id) : [])} />
              <tbody>{szListe.map((tn) => <SZZeile key={tn.id} tn={tn} ausgewaehlt={sz.ids.includes(tn.id)} toggle={() => sz.toggle(tn.id)} onSaved={laden} />)}</tbody>
            </table>
            {!szListe.length && <p className="text-sm text-[#8E7B71] p-4">Keine Einträge.</p>}
          </div>
        </TabsContent>
      </Tabs>
      <p className="text-[11px] text-[#8E7B71] mt-4 flex items-center gap-1"><Mail size={12} /> Wann eine Rechnung verschickt wurde, siehst du in der Rechnungsliste am grünen Brief-Symbol mit Datum.</p>
    </div>
  );
}
